/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });

            this.removeBtn = this.querySelector('[data-quick-qty-remove]');
            if (this.removeBtn) {
              this.removeBtn.addEventListener('click', (evt) => {
                evt.preventDefault();
                this.updateQuantity(0);
              });
            }
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const inventory = JSON.parse(dataEl.textContent);

            if (inventory) {
              if (inventory.inventory_management && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
                quantityInput.max = inventory.inventory_quantity;

                const currentQuantity = Number(quantityInput.value);
                if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                  quantityInput.value = quantityInput.max;
                }
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          // sections.push({
          //   id: `quick-order-list_${sectionId}`,
          //   section: sectionId,
          //   selector: `#quick-order-list_${sectionId}`
          // });
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                  || theme.settings.afterAtc === 'drawer')
              );

              // Update the quick order lists on the page
              if (quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;

                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    data.sections[quickOrderSection.section],
                    quickOrderSection.selector
                  );
                });
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        let newQuantity = 1;

        // Check if product was already in the cart
        if (itemInOldCart) {
          newQuantity = Number(data.quantity) || itemInOldCart.quantity;
          if (this.quantitySelector) {
            this.quantityInput.dataset.initialValue = String(newQuantity);
            this.updateQuantityState(newQuantity);
          }
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));

          // Update the quantity selectors if needed
          if (this.quantitySelector) {
            this.quantityInput.dataset.initialValue = String(newQuantity);
            this.updateQuantityState(newQuantity);
          }
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');
      const requestedQty = parseInt(quantity, 10);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: String(this.dataset.variantId),
          quantity: requestedQty
        })
      };

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || data.description || data.message || response.status);

        const lineItem = Array.isArray(data.items)
          ? data.items.find((item) => String(item.variant_id) === String(this.dataset.variantId))
          : null;
        const actualQty = lineItem ? Number(lineItem.quantity) : requestedQty;

        if (lineErrors) {
          if (actualQty < requestedQty) {
            lineErrors.textContent = data.message || data.description || theme.strings.cartError;
            lineErrors.hidden = false;
          } else {
            lineErrors.innerHTML = '';
            lineErrors.hidden = true;
          }
        }

        this.quantityInput.dataset.initialValue = String(actualQty);
        this.updateQuantityState(actualQty);
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } catch (error) {
        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }
        console.log(error); // eslint-disable-line

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10) || 0;
        this.updateQuantityState(oldQuantity);
      } finally {
        this.quantitySelector.removeAttribute('aria-disabled');
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}

document.addEventListener('click', (event) => {
  const stepBtn = event.target.closest('[data-dimark-qty]');
  if (!stepBtn) return;

  const root = stepBtn.closest('.dimark-atc');
  if (!root) return;

  const input = root.querySelector('.dimark-atc__input');
  if (!input) return;

  event.preventDefault();
  const delta = stepBtn.getAttribute('data-dimark-qty') === 'plus' ? 1 : -1;
  const min = Number(input.min) || 1;
  const max = Number(input.max) || 9999;
  const next = Math.min(max, Math.max(min, (Number(input.value) || 1) + delta));
  input.value = String(next);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const stepBtn = event.target.closest('[data-dimark-qty]');
  if (!stepBtn) return;
  event.preventDefault();
  stepBtn.click();
});

const formatDimarkMoney = (cents, format) => {
  const value = (Math.round(Number(cents) || 0) / 100).toFixed(2);
  const grouped = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const safeFormat = String(format || '£{{amount}}')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!safeFormat) return `£${grouped}`;
  return safeFormat
    .replace(/\{\{\s*amount\s*\}\}/g, grouped)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grouped.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)));
};

const updateDimarkAtcCard = (card) => {
  const input = card.querySelector('.dimark-atc__input');
  const qty = Math.max(1, Number(input && input.value) || 1);
  const unitPrice = Number(card.dataset.dimarkUnitPrice) || 0;
  const unitsPerCase = parseInt(card.dataset.dimarkUnitQty, 10) || 0;

  const totalEl = card.querySelector('[data-dimark-atc-total]');
  if (totalEl && unitPrice) {
    totalEl.textContent = `Add ${formatDimarkMoney(unitPrice * qty, card.dataset.dimarkMoneyFormat)}`;
  }

  const summaryEl = card.querySelector('[data-dimark-atc-summary]');
  if (!summaryEl) return;

  const caseLabel = qty === 1 ? 'case' : 'cases';
  const parts = [`${qty} ${caseLabel}`];
  if (unitsPerCase > 0) {
    parts[0] += ` = ${qty * unitsPerCase} units`;
  }
  summaryEl.textContent = parts.join(' · ');
};

const bindDimarkAtcCards = () => {
  document.querySelectorAll('.dimark-card-layout[data-dimark-unit-price]').forEach((card) => {
    updateDimarkAtcCard(card);
    const input = card.querySelector('.dimark-atc__input');
    if (!input || input.dataset.dimarkBound) return;
    input.dataset.dimarkBound = '1';
    input.addEventListener('change', () => updateDimarkAtcCard(card));
    input.addEventListener('input', () => updateDimarkAtcCard(card));
  });
};

const getDimarkCardVariantId = (card) => {
  const idInput = card.querySelector('input[name="id"]');
  return idInput ? String(idInput.value) : '';
};

const resetDimarkAtcQty = (card) => {
  const input = card.querySelector('.dimark-atc__input');
  if (!input) return;

  const min = Number(input.min) || 1;
  if (Number(input.value) !== min) {
    input.value = String(min);
  }
  updateDimarkAtcCard(card);
};

const resetDimarkAtcForVariant = (variantId) => {
  if (variantId == null || variantId === '') return;
  const id = String(variantId);

  document.querySelectorAll('.dimark-card-layout').forEach((card) => {
    if (getDimarkCardVariantId(card) !== id) return;
    resetDimarkAtcQty(card);
  });
};

const bindDimarkAtcCartSync = () => {
  if (document.documentElement.dataset.dimarkAtcCartSync === '1') return;
  document.documentElement.dataset.dimarkAtcCartSync = '1';

  document.addEventListener('on:line-item:change', (event) => {
    const detail = event.detail || {};
    if (Number(detail.newQuantity) > 0) return;
    resetDimarkAtcForVariant(detail.variantId);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindDimarkAtcCards();
    bindDimarkAtcCartSync();
  }, { once: true });
} else {
  bindDimarkAtcCards();
  bindDimarkAtcCartSync();
}
